import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Play, RefreshCw, Settings, X } from 'lucide-react';
import { PermissionGuard, usePermissions } from '@components/PermissionGuard';
import DateInputBR from '@components/DateInputBR';
import processService from '../../services/processService';
import {
  ActionStatus,
  ExecStatus,
  ProcessKey,
  ProcessMode,
  TriggerType,
  type Process,
  type ProcessAction,
  type ProcessExecution,
  type ProcessRunPayload,
  type ProcessUpdatePayload,
} from '../../types/process';

// ── status helpers ──

const execStatusMeta = (s: number): { label: string; cls: string } => {
  switch (s) {
    case ExecStatus.Pending:
      return { label: 'Pendente', cls: 'bg-gray-100 text-gray-700' };
    case ExecStatus.Running:
      return { label: 'Em execução', cls: 'bg-blue-100 text-blue-700' };
    case ExecStatus.Success:
      return { label: 'Sucesso', cls: 'bg-green-100 text-green-700' };
    case ExecStatus.PartialError:
      return { label: 'Erro parcial', cls: 'bg-amber-100 text-amber-800' };
    case ExecStatus.Failed:
      return { label: 'Falha', cls: 'bg-red-100 text-red-700' };
    case ExecStatus.AwaitingApproval:
      return { label: 'Aguardando aprovação', cls: 'bg-purple-100 text-purple-700' };
    case ExecStatus.Cancelled:
      return { label: 'Cancelada', cls: 'bg-gray-100 text-gray-500' };
    default:
      return { label: `Status ${s}`, cls: 'bg-gray-100 text-gray-700' };
  }
};

/** Execução que terminou bem, mas deixou alvos pedindo ação humana, é outra leitura de "Sucesso". */
const execBadgeMeta = (e: { status: number; alertCount?: number }): { label: string; cls: string } =>
  e.status === ExecStatus.Success && (e.alertCount ?? 0) > 0
    ? { label: 'Concluído com alertas', cls: 'bg-amber-100 text-amber-800' }
    : execStatusMeta(e.status);

const actionStatusMeta = (s: number): { label: string; cls: string } => {
  switch (s) {
    case ActionStatus.Pending:
      return { label: 'Pendente', cls: 'bg-gray-100 text-gray-700' };
    case ActionStatus.Success:
      return { label: 'Concluída', cls: 'bg-green-100 text-green-700' };
    case ActionStatus.Failed:
      return { label: 'Falha', cls: 'bg-red-100 text-red-700' };
    case ActionStatus.Skipped:
      return { label: 'Ignorada', cls: 'bg-gray-100 text-gray-500' };
    case ActionStatus.Alert:
      return { label: 'Alerta', cls: 'bg-amber-100 text-amber-800' };
    default:
      return { label: `Status ${s}`, cls: 'bg-gray-100 text-gray-700' };
  }
};

const fmtDateTime = (s: string | null): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

const Badge: React.FC<{ meta: { label: string; cls: string } }> = ({ meta }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>{meta.label}</span>
);

const ProcessExecutionView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('processes.write').hasPermission || checkPermission('*').hasPermission;
  const canExecute = checkPermission('processes.execute').hasPermission || checkPermission('*').hasPermission;

  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [executions, setExecutions] = useState<Record<number, ProcessExecution[]>>({});
  const [loadingExec, setLoadingExec] = useState<Record<number, boolean>>({});
  const [busy, setBusy] = useState(false);

  const [configProcess, setConfigProcess] = useState<Process | null>(null);
  const [reprocessProcess, setReprocessProcess] = useState<Process | null>(null);
  const [actionsModal, setActionsModal] = useState<{ execId: number; actions: ProcessAction[] } | null>(null);
  const [approvalModal, setApprovalModal] = useState<{
    execution: ProcessExecution;
    actions: ProcessAction[];
    selected: Set<number>;
  } | null>(null);

  const loadProcesses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProcesses(await processService.listProcesses());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar processos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProcesses();
  }, [loadProcesses]);

  const loadExecutions = useCallback(async (processId: number) => {
    setLoadingExec((m) => ({ ...m, [processId]: true }));
    try {
      const execs = await processService.listExecutions(processId);
      setExecutions((m) => ({ ...m, [processId]: execs }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar execuções');
    } finally {
      setLoadingExec((m) => ({ ...m, [processId]: false }));
    }
  }, []);

  const toggleExpand = (p: Process) => {
    if (expanded === p.id) {
      setExpanded(null);
      return;
    }
    setExpanded(p.id);
    if (!executions[p.id]) loadExecutions(p.id);
  };

  const handleRun = async (p: Process) => {
    if (!canExecute) return;
    // bi_daily accepts an optional list of reprocess dates on a manual run —
    // collect them in a dedicated modal before triggering.
    if (p.executionMode !== ProcessMode.ManualApproval && p.processKey === ProcessKey.BIDaily) {
      setReprocessProcess(p);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (p.executionMode === ProcessMode.ManualApproval) {
        const { execution, actions } = await processService.preExecute(p.id);
        setApprovalModal({ execution, actions, selected: new Set(actions.map((a) => a.id)) });
      } else {
        await processService.runProcess(p.id);
      }
      setExpanded(p.id);
      await loadExecutions(p.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao executar processo');
    } finally {
      setBusy(false);
    }
  };

  const runWithPayload = async (p: Process, payload?: ProcessRunPayload) => {
    setBusy(true);
    setError(null);
    try {
      await processService.runProcess(p.id, payload);
      setReprocessProcess(null);
      setExpanded(p.id);
      await loadExecutions(p.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao executar processo');
    } finally {
      setBusy(false);
    }
  };

  const openActions = async (execId: number) => {
    setBusy(true);
    try {
      const actions = await processService.listActions(execId);
      setActionsModal({ execId, actions });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar ações');
    } finally {
      setBusy(false);
    }
  };

  const openApproval = async (execution: ProcessExecution) => {
    setBusy(true);
    try {
      const actions = await processService.listActions(execution.id);
      setApprovalModal({ execution, actions, selected: new Set(actions.map((a) => a.id)) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar ações');
    } finally {
      setBusy(false);
    }
  };

  const submitApproval = async () => {
    if (!approvalModal) return;
    setBusy(true);
    try {
      await processService.approve(approvalModal.execution.id, Array.from(approvalModal.selected));
      const processId = approvalModal.execution.processId;
      setApprovalModal(null);
      await loadExecutions(processId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao aprovar execução');
    } finally {
      setBusy(false);
    }
  };

  const cancelExecution = async (exec: ProcessExecution) => {
    setBusy(true);
    try {
      await processService.cancel(exec.id);
      await loadExecutions(exec.processId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao cancelar execução');
    } finally {
      setBusy(false);
    }
  };

  const saveConfig = async (payload: ProcessUpdatePayload) => {
    if (!configProcess) return;
    setBusy(true);
    try {
      await processService.updateProcess(configProcess.id, payload);
      setConfigProcess(null);
      await loadProcesses();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar configuração');
    } finally {
      setBusy(false);
    }
  };

  return (
    <PermissionGuard permission="processes.read" fallback={<div className="p-6 text-slate-600">Acesso negado.</div>}>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Execução de Processos</h1>
            <p className="text-sm text-slate-500">Processos agendados e sob demanda do ERP.</p>
          </div>
          <button
            onClick={loadProcesses}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" /> Atualizar
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 rounded-lg bg-slate-200 animate-pulse" />
            ))}
          </div>
        ) : processes.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
            Nenhum processo cadastrado.
          </div>
        ) : (
          <div className="space-y-3">
            {processes.map((p) => {
              const isOpen = expanded === p.id;
              const execs = executions[p.id] || [];
              return (
                <div key={p.id} className="rounded-lg border border-slate-200 bg-white">
                  <div className="flex items-start justify-between gap-3 p-4">
                    <button className="flex items-start gap-2 text-left" onClick={() => toggleExpand(p)}>
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4 mt-1 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 mt-1 text-slate-400" />
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{p.name}</span>
                          <Badge
                            meta={
                              p.executionMode === ProcessMode.ManualApproval
                                ? { label: 'Aprovação Manual', cls: 'bg-purple-100 text-purple-700' }
                                : { label: 'Automático', cls: 'bg-blue-100 text-blue-700' }
                            }
                          />
                          {!p.isActive && <Badge meta={{ label: 'Inativo', cls: 'bg-gray-100 text-gray-500' }} />}
                          {p.scheduledTime && (
                            <Badge meta={{ label: `Agendado ${p.scheduledTime}`, cls: 'bg-slate-100 text-slate-600' }} />
                          )}
                        </div>
                        {p.description && <p className="text-sm text-slate-500 mt-0.5">{p.description}</p>}
                      </div>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      {canWrite && (
                        <button
                          onClick={() => setConfigProcess(p)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                        >
                          <Settings className="w-4 h-4" /> Configurar
                        </button>
                      )}
                      {canExecute && (
                        <button
                          onClick={() => handleRun(p)}
                          disabled={busy || !p.isActive}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          <Play className="w-4 h-4" />
                          {p.executionMode === ProcessMode.ManualApproval ? 'Pré-executar' : 'Executar agora'}
                        </button>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-slate-100 p-4">
                      {loadingExec[p.id] ? (
                        <div className="h-16 rounded bg-slate-100 animate-pulse" />
                      ) : execs.length === 0 ? (
                        <p className="text-sm text-slate-500">Nenhuma execução registrada.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-left text-slate-500 border-b border-slate-100">
                                <th className="py-2 pr-3 font-medium">Data</th>
                                <th className="py-2 pr-3 font-medium">Status</th>
                                <th className="py-2 pr-3 font-medium">Disparo</th>
                                <th className="py-2 pr-3 font-medium">Resultados</th>
                                <th className="py-2 pr-3 font-medium">Início</th>
                                <th className="py-2 pr-3 font-medium">Fim</th>
                                <th className="py-2 pr-3 font-medium text-right">Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {execs.map((e) => (
                                <tr key={e.id} className="border-b border-slate-50 align-top">
                                  <td className="py-2 pr-3 text-slate-700">{e.executionDate}</td>
                                  <td className="py-2 pr-3">
                                    <Badge meta={execBadgeMeta(e)} />
                                    {e.errorMessage && (
                                      <p className="text-xs text-red-600 mt-1 max-w-xs">{e.errorMessage}</p>
                                    )}
                                  </td>
                                  <td className="py-2 pr-3 text-slate-600">
                                    {e.triggerType === TriggerType.Scheduled ? 'Agendado' : 'Manual'}
                                  </td>
                                  <td className="py-2 pr-3">
                                    <span className="text-slate-700">{e.totalTargets} alvos</span>{' '}
                                    <span className="text-green-700">{e.successCount}✓</span>{' '}
                                    <span className="text-red-700">{e.failureCount}✕</span>{' '}
                                    <span className="text-slate-500">{e.skippedCount}↷</span>
                                    {(e.alertCount ?? 0) > 0 && (
                                      <>
                                        {' '}
                                        <span className="text-amber-700" title="Alertas: pedem ação humana, não são falha do processo">
                                          {e.alertCount}⚠
                                        </span>
                                      </>
                                    )}
                                  </td>
                                  <td className="py-2 pr-3 text-slate-600">{fmtDateTime(e.startedAt)}</td>
                                  <td className="py-2 pr-3 text-slate-600">{fmtDateTime(e.finishedAt)}</td>
                                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                                    {e.status === ExecStatus.AwaitingApproval && canExecute && (
                                      <>
                                        <button
                                          onClick={() => openApproval(e)}
                                          className="text-blue-600 hover:underline mr-3"
                                        >
                                          Aprovar
                                        </button>
                                        <button
                                          onClick={() => cancelExecution(e)}
                                          className="text-red-600 hover:underline mr-3"
                                        >
                                          Cancelar
                                        </button>
                                      </>
                                    )}
                                    <button onClick={() => openActions(e.id)} className="text-slate-600 hover:underline">
                                      Ver ações
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {configProcess && (
        <ConfigModal process={configProcess} busy={busy} onClose={() => setConfigProcess(null)} onSave={saveConfig} />
      )}

      {reprocessProcess && (
        <ReprocessModal
          process={reprocessProcess}
          busy={busy}
          onClose={() => setReprocessProcess(null)}
          onRun={(from, to) =>
            runWithPayload(
              reprocessProcess,
              from && to ? { reprocessFrom: from, reprocessTo: to } : undefined,
            )
          }
        />
      )}

      {actionsModal && (
        <Modal title="Ações da execução" onClose={() => setActionsModal(null)}>
          {actionsModal.actions.length === 0 ? (
            <p className="text-sm text-slate-500">
              Nenhuma ação registrada (execuções automáticas só registram falhas e alertas).
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3 font-medium">Alvo</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {actionsModal.actions.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50 align-top">
                    <td className="py-2 pr-3 text-slate-700">{a.targetLabel || a.targetRef || '—'}</td>
                    <td className="py-2 pr-3">
                      <Badge meta={actionStatusMeta(a.status)} />
                    </td>
                    <td className={`py-2 pr-3 ${a.status === ActionStatus.Alert ? 'text-amber-700' : 'text-red-600'}`}>
                      {a.errorMessage || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {approvalModal && (
        <Modal title="Aprovar execução" onClose={() => setApprovalModal(null)}>
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-slate-600">
              {approvalModal.selected.size} de {approvalModal.actions.length} selecionado(s)
            </span>
            <button
              onClick={() =>
                setApprovalModal((m) =>
                  m
                    ? {
                        ...m,
                        selected:
                          m.selected.size === m.actions.length
                            ? new Set()
                            : new Set(m.actions.map((a) => a.id)),
                      }
                    : m,
                )
              }
              className="text-blue-600 hover:underline"
            >
              {approvalModal.selected.size === approvalModal.actions.length ? 'Limpar seleção' : 'Selecionar todos'}
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto border border-slate-100 rounded">
            {approvalModal.actions.map((a) => (
              <label key={a.id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-50 text-sm">
                <input
                  type="checkbox"
                  checked={approvalModal.selected.has(a.id)}
                  onChange={(ev) =>
                    setApprovalModal((m) => {
                      if (!m) return m;
                      const sel = new Set(m.selected);
                      if (ev.target.checked) sel.add(a.id);
                      else sel.delete(a.id);
                      return { ...m, selected: sel };
                    })
                  }
                />
                <span className="text-slate-700">{a.targetLabel || a.targetRef || `#${a.id}`}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => cancelExecution(approvalModal.execution).then(() => setApprovalModal(null))}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Cancelar execução
            </button>
            <button
              onClick={submitApproval}
              disabled={busy || approvalModal.selected.size === 0}
              className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Aprovar e enviar
            </button>
          </div>
        </Modal>
      )}
    </PermissionGuard>
  );
};

// ── modals ──

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="font-semibold text-slate-800">{title}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

const ConfigModal: React.FC<{
  process: Process;
  busy: boolean;
  onClose: () => void;
  onSave: (payload: ProcessUpdatePayload) => void;
}> = ({ process, busy, onClose, onSave }) => {
  const [scheduledTime, setScheduledTime] = useState(process.scheduledTime ?? '');
  const [executionMode, setExecutionMode] = useState(process.executionMode);
  const [isActive, setIsActive] = useState(process.isActive);

  return (
    <Modal title={`Configurar — ${process.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Horário agendado (opcional)</label>
          <input
            type="time"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            className="w-40 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
          <p className="text-xs text-slate-500 mt-1">Em branco = somente sob demanda.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Modo de execução</label>
          <select
            value={executionMode}
            onChange={(e) => setExecutionMode(Number(e.target.value))}
            className="w-56 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          >
            <option value={ProcessMode.Automatic}>Automático</option>
            <option value={ProcessMode.ManualApproval}>Aprovação Manual</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Ativo
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
          Cancelar
        </button>
        <button
          onClick={() => onSave({ scheduledTime: scheduledTime || null, executionMode, isActive })}
          disabled={busy}
          className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Salvar
        </button>
      </div>
    </Modal>
  );
};

// ── reprocessamento por intervalo (bi_daily) ──

const getToday = () => new Date().toISOString().split('T')[0];

const getFirstOfCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const getFirstOfPreviousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Teto do intervalo aceito pelo backend (domain.MaxReprocessRangeDays). Validar
// aqui evita uma ida ao servidor só para receber o 400.
const MAX_REPROCESS_RANGE_DAYS = 400;

const daysBetweenInclusive = (from: string, to: string): number => {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.floor((b - a) / 86400000) + 1;
};

const fmtDateBR = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

// ReprocessModal coleta um INTERVALO de dias para forçar o reprocessamento do
// BI (bi_daily). O intervalo vai como reprocessFrom/reprocessTo e o backend o
// expande em dias na fila [BIDomain].[ProcessOrdersDay] antes do survey.
// Executar sem datas é uma execução normal (dias pendentes e reprocessamentos já
// agendados).
const ReprocessModal: React.FC<{
  process: Process;
  busy: boolean;
  onClose: () => void;
  onRun: (from: string, to: string) => void;
}> = ({ process, busy, onClose, onRun }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const applyPreset = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const hasRange = dateFrom !== '' && dateTo !== '';
  const partial = (dateFrom === '') !== (dateTo === '');
  const totalDays = hasRange ? daysBetweenInclusive(dateFrom, dateTo) : 0;
  const inverted = hasRange && totalDays <= 0;
  const tooLong = totalDays > MAX_REPROCESS_RANGE_DAYS;
  const blocked = partial || inverted || tooLong;

  return (
    <Modal title={`Executar — ${process.name}`} onClose={onClose}>
      <p className="text-sm text-slate-600">
        Informe um intervalo para reprocessar os pedidos de venda daqueles dias: os dados do BI do
        período são <strong>limpos e gerados novamente</strong> a partir dos pedidos atuais, incluindo
        os totais mensais dos meses tocados.
      </p>
      <p className="text-sm text-slate-600 mt-2">
        Deixe as datas em branco para uma execução normal (dias pendentes e reprocessamentos já
        agendados).
      </p>

      <div className="mt-4">
        <label className="block text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">
          Período a reprocessar
        </label>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Data inicial</label>
            <DateInputBR
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={setDateFrom}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Data final</label>
            <DateInputBR
              className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={setDateTo}
            />
          </div>
          {hasRange && (
            <button
              onClick={() => applyPreset('', '')}
              className="px-2.5 py-1.5 text-xs rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50"
            >
              Limpar
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={() => applyPreset(getToday(), getToday())}
            className="px-2.5 py-1 text-xs rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            Hoje
          </button>
          <button
            onClick={() => applyPreset(getFirstOfCurrentMonth(), getToday())}
            className="px-2.5 py-1 text-xs rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            Mês atual
          </button>
          <button
            onClick={() => applyPreset(getFirstOfPreviousMonth(), getToday())}
            className="px-2.5 py-1 text-xs rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
          >
            Desde o mês passado
          </button>
        </div>
      </div>

      {partial && (
        <p className="mt-3 text-sm text-amber-700">Informe as duas datas do intervalo.</p>
      )}
      {inverted && (
        <p className="mt-3 text-sm text-amber-700">
          A data final não pode ser anterior à inicial.
        </p>
      )}
      {tooLong && (
        <p className="mt-3 text-sm text-amber-700">
          Intervalo muito grande ({totalDays} dias). O máximo é {MAX_REPROCESS_RANGE_DAYS} dias.
        </p>
      )}
      {hasRange && !blocked && (
        <p className="mt-3 text-sm text-slate-500">
          {totalDays === 1
            ? `Será reprocessado o dia ${fmtDateBR(dateFrom)}.`
            : `Serão reprocessados ${totalDays} dias, de ${fmtDateBR(dateFrom)} a ${fmtDateBR(dateTo)}.`}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          Cancelar
        </button>
        <button
          onClick={() => onRun(dateFrom, dateTo)}
          disabled={busy || blocked}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          <Play className="w-4 h-4" />
          {hasRange && !blocked
            ? `Reprocessar ${totalDays} dia(s)`
            : 'Executar agora'}
        </button>
      </div>
    </Modal>
  );
};

export default ProcessExecutionView;
